import { useLingui } from "@lingui/react/macro";
import { FileTextIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { Tooltip } from "@/components/ui-kit/base/tooltip";
import { CellOverflowList } from "@/components/ui-kit/tables/cells/cell-overflow-list";

import type { DailyMonitoringModel, EcgReport } from "./daily-monitoring-model";

/**
 * Upload time, 24h. Every report in the cell was recorded on the day being
 * shown, so the date they share identifies nothing and the time is the only
 * thing that tells two of a patient's ECGs apart — the file names differ by a
 * digit, if at all. The active app locale supplies the separator.
 */
const UPLOAD_TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/**
 * How many reports sit in the cell before the rest go behind a "+N" popover.
 * One: the newest is the one a reviewer opens, and a second bare icon beside
 * it would say nothing about which is which.
 */
const MAX_INLINE = 1;

/**
 * ECG report cell for a worklist row. Lazily loads the patient's ECG report
 * files on mount, then shows the newest as an icon that opens it, with any
 * others from the same day behind a "+N" popover — the Physician(s) column's
 * shape in the patients table, one inline and the rest a click away.
 *
 * An icon rather than a label: the file name is long, near-identical between a
 * patient's own uploads, and answers neither question the reviewer has here —
 * whether there is an ECG today, and how to open it. The popover rows do carry
 * the upload time, which is what lets the reader pick one from another.
 *
 * `downloadBase` is `seerlinqApi.versionedRoute()`.
 */
export const MonitoringEcgCell = observer(function MonitoringEcgCell({
  model,
  patientId,
  downloadBase,
}: {
  model: DailyMonitoringModel;
  patientId: number;
  downloadBase: string;
}) {
  useEffect(() => {
    void model.ensureEcgReports(patientId);
  }, [model, patientId]);

  const testId = `dm-ecg-${patientId}`;
  const entry = model.ecgReports.get(patientId);

  if (!entry || entry.status === "loading") {
    return <span className="text-muted-foreground">…</span>;
  }
  if (entry.status === "error" || entry.reports.length === 0) {
    return (
      <span data-testid={testId} className="text-muted-foreground">
        –
      </span>
    );
  }

  // Newest first, so the report inline is the latest of the day. Ordered by
  // upload time: the cell only ever holds reports dated the day being shown,
  // so their dates are all equal.
  const reports = [...entry.reports].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  return (
    <span data-testid={testId}>
      <CellOverflowList
        maxInline={MAX_INLINE}
        separator=" "
        items={reports.map((report, index) => (
          <EcgReportLink
            key={report.uuid}
            report={report}
            href={`${downloadBase}/medical-reports/download/${report.uuid}`}
            // Everything past `MAX_INLINE` is rendered in the popover — where
            // there is room for the time, and need of it: a stack of bare
            // icons gives the reader nothing to choose between.
            withTime={index >= MAX_INLINE}
          />
        ))}
      />
    </span>
  );
});

/**
 * One report, as a file icon that opens it. Inline the icon stands alone and
 * the upload time is its tooltip; in the popover the time is shown beside it.
 */
function EcgReportLink({
  report,
  href,
  withTime,
}: {
  report: EcgReport;
  href: string;
  withTime: boolean;
}) {
  const { i18n, t } = useLingui();
  const time = new Intl.DateTimeFormat(i18n.locale, UPLOAD_TIME_OPTS).format(
    new Date(report.created_at),
  );
  const label = t`ECG uploaded at ${time}`;

  const link = (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="inline-flex items-center gap-1 align-middle text-brand-primary"
    >
      <FileTextIcon size={14} className="shrink-0" />
      {withTime && <span className="tabular-nums">{time}</span>}
    </a>
  );

  // In the popover the time is already on screen; a tooltip would only repeat
  // it.
  return withTime ? link : <Tooltip content={label}>{link}</Tooltip>;
}
